import { useState } from 'react';
import type { AvatarVariant, GameModules, Preset } from '@schemer/shared';
import { AvatarPicker } from './AvatarPicker.js';
import { IMPLEMENTED_MODULES, MODULE_BLURBS, MODULE_LABELS, PRESET_MODULES } from '@schemer/shared';
import { api, ApiError } from '../lib/api.js';
import { nameStore, tokenStore } from '../lib/storage.js';
import { navigate } from '../lib/route.js';

const DEFAULT_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Amsterdam';

// Presets = one tap that bundles modules (modus-en-modules.md). 'eigen' = custom.
// `depth` (1..4) drives the little rising complexity meter on each card; the four
// named presets are a real ladder from bare basics to almost-everything. 'eigen'
// is off-ladder (depth 0 → shown as a "vrij" chip instead).
const PRESET_OPTIONS: ReadonlyArray<{ value: Preset; label: string; desc: string; depth: number }> =
  [
    {
      value: 'introductie',
      label: 'Introductie',
      desc: 'De kale basis. Voor een eerste potje.',
      depth: 1,
    },
    {
      value: 'familie',
      label: 'Familie',
      desc: 'Locatie-chats + Ziener. Comfortabel, nog simpel.',
      depth: 2,
    },
    {
      value: 'klassiek',
      label: 'Klassiek',
      desc: 'Meer diepte: inzet, alle specials, en meer.',
      depth: 3,
    },
    {
      value: 'uitdaging',
      label: 'Uitdaging',
      desc: 'Bijna alles aan, inclusief de schade-meter.',
      depth: 4,
    },
    { value: 'eigen', label: 'Eigen', desc: 'Zet zelf elke module aan of uit.', depth: 0 },
  ];

const MODULE_KEYS = Object.keys(MODULE_LABELS) as (keyof GameModules)[];

export function CreateScreen(): React.JSX.Element {
  const [displayName, setDisplayName] = useState(nameStore.get() ?? '');
  const [avatar, setAvatar] = useState<AvatarVariant | null>(null);
  const [minPlayers, setMinPlayers] = useState(4);
  const [tz, setTz] = useState(DEFAULT_TZ);
  const [eveningStart, setEveningStart] = useState('20:00');
  const [clockMode, setClockMode] = useState<'realtime' | 'fast'>('realtime');
  const [preset, setPreset] = useState<Preset>('introductie');
  // Custom toggles — only used when preset === 'eigen'.
  const [custom, setCustom] = useState<Partial<GameModules>>({});
  // Depth/tuning: the systemic nat-hout frequency (only sent when natHout is on).
  const [natHoutChance, setNatHoutChance] = useState(0.5);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(key: keyof GameModules, on: boolean) {
    setCustom((c) => ({ ...c, [key]: on }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.createGame({
        displayName: displayName.trim(),
        minPlayers,
        ...(avatar ? { avatar } : {}),
        schedule: { timezone: tz, eveningStart, clockMode },
        // Named preset → send preset. Eigen → send the explicit toggles.
        ...(preset === 'eigen' ? { preset, modules: custom } : { preset }),
        // Only meaningful with natHout on — but harmless to send otherwise.
        ...(natHoutOn ? { natHoutChance } : {}),
      });
      tokenStore.set(res.deviceToken);
      nameStore.set(displayName.trim());
      navigate({ name: 'lobby', code: res.lobby.code });
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : 'er ging iets mis');
    } finally {
      setSubmitting(false);
    }
  }

  // Which modules the chosen named preset switches on (for the summary).
  const enabled = MODULE_KEYS.filter((k) => PRESET_MODULES[preset][k]);
  // Is systemic nat hout on for the current choice? (drives the tuning slider)
  const natHoutOn = preset === 'eigen' ? custom.natHout === true : PRESET_MODULES[preset].natHout;
  const chancePct = Math.round(natHoutChance * 100);
  const chanceWord =
    natHoutChance <= 0.25 ? 'zeldzaam' : natHoutChance >= 0.6 ? 'vaak' : 'af en toe';

  return (
    <main className="form-screen">
      <button className="back" onClick={() => navigate({ name: 'splash' })}>
        ← terug
      </button>
      <h1>Nieuw spel starten</h1>
      <form onSubmit={onSubmit}>
        <label>
          Jouw naam
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={24}
            required
            autoFocus
          />
        </label>

        <AvatarPicker value={avatar} onChange={setAvatar} disabled={submitting} />

        <fieldset className="modules">
          <legend>Spelmodus</legend>
          <div className="preset-grid">
            {PRESET_OPTIONS.map((opt) => (
              <label className={`preset-opt${preset === opt.value ? ' sel' : ''}`} key={opt.value}>
                <input
                  type="radio"
                  name="preset"
                  checked={preset === opt.value}
                  onChange={() => setPreset(opt.value)}
                />
                <span className="po-dot" aria-hidden="true" />
                <span className="po-body">
                  <span className="po-head">
                    <b className="po-name">{opt.label}</b>
                    {opt.depth > 0 ? (
                      <span
                        className="po-level"
                        aria-label={`diepte ${opt.depth} van 4`}
                        title={`diepte ${opt.depth}/4`}
                      >
                        {[1, 2, 3, 4].map((i) => (
                          <i key={i} className={i <= opt.depth ? 'on' : ''} />
                        ))}
                      </span>
                    ) : (
                      <span className="po-free">vrij</span>
                    )}
                  </span>
                  <span className="po-desc">{opt.desc}</span>
                </span>
              </label>
            ))}
          </div>

          {preset === 'eigen' ? (
            <div className="preset-custom">
              <p className="hint">Zet losse modules aan — nog niet gebouwde tonen “binnenkort”:</p>
              <div className="mod-list">
                {MODULE_KEYS.map((key) => {
                  const soon = !IMPLEMENTED_MODULES.has(key);
                  const on = custom[key] === true && !soon;
                  return (
                    <label className={`mod-opt${on ? ' on' : ''}${soon ? ' soon' : ''}`} key={key}>
                      <input
                        type="checkbox"
                        checked={custom[key] === true}
                        disabled={soon}
                        onChange={(e) => toggle(key, e.target.checked)}
                      />
                      <span className="mod-text">
                        <span className="mod-name">
                          {MODULE_LABELS[key]}
                          {soon && <span className="soon-tag">binnenkort</span>}
                        </span>
                        <span className="mod-desc">{MODULE_BLURBS[key]}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="preset-summary">
              <p className="hint">Deze modus zet aan:</p>
              {enabled.length === 0 ? (
                <p className="summary-none">Niets: de kale basis (Introductiemodus).</p>
              ) : (
                <div className="chip-row">
                  {enabled.map((key) => (
                    <span
                      className={`chip${!IMPLEMENTED_MODULES.has(key) ? ' soon-chip' : ''}`}
                      key={key}
                    >
                      {MODULE_LABELS[key]}
                      {!IMPLEMENTED_MODULES.has(key) && <em> binnenkort</em>}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </fieldset>

        {natHoutOn && (
          <fieldset className="modules tuning">
            <legend>Afstelling — nat hout</legend>
            <p className="hint">
              Hoe vaak laat het weer een trouw vuur sputteren? Laag houden — te veel wordt
              frustrerend.
            </p>
            <label className="slider">
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={natHoutChance}
                onChange={(e) => setNatHoutChance(Number(e.target.value))}
              />
              <span className="slider-val">
                {chanceWord} · ~{chancePct}% per nacht
              </span>
            </label>
          </fieldset>
        )}

        {error && <p className="bad">{error}</p>}
        {/* 6 · Progressive disclosure: a casual host should meet name, character
            and game mode first — not timezone and clock mode. These four all have
            working defaults, so they stay out of the way until asked for.
            7 · Klokmodus lives here too: realtime IS the default, fast is a test knob. */}
        <details className="advanced">
          <summary>Geavanceerd — je kunt dit gerust overslaan</summary>
          <div className="advanced-body">
            <label>
              Minimaal aantal spelers om te starten
              <input
                type="number"
                min={4}
                max={20}
                value={minPlayers}
                onChange={(e) => setMinPlayers(Number(e.target.value))}
              />
            </label>

            <label>
              Tijdzone (voor het dagritme)
              <input type="text" value={tz} onChange={(e) => setTz(e.target.value)} />
            </label>

            <label>
              Avond / stemtijd (lokaal)
              <input
                type="time"
                value={eveningStart}
                onChange={(e) => setEveningStart(e.target.value)}
              />
            </label>

            <label>
              Klokmodus
              <select
                value={clockMode}
                onChange={(e) => setClockMode(e.target.value as 'realtime' | 'fast')}
              >
                <option value="realtime">Realtime (dagen)</option>
                <option value="fast">Snel (test)</option>
              </select>
            </label>
          </div>
        </details>

        <button className="cta primary" type="submit" disabled={submitting || !displayName.trim()}>
          {submitting ? 'aanmaken…' : 'spel aanmaken'}
        </button>
      </form>
    </main>
  );
}
